// ============================================================================
// BP-13-02: Admin API Client
// BrightPath Platform - Admin Frontend
// ============================================================================

import apiClient from '@/core/lib/api-client';
import type {
  // Users
  AdminUser,
  AdminUserListParams,
  AdminUserListResponse,
  UpdateAdminUser,
  SuspendUserRequest,
  UserActivityListParams,
  UserActivityListResponse,
  UserStats,
  // Moderation
  ModerationItem,
  ModerationQueueParams,
  ModerationQueueResponse,
  ModerationActionRequest,
  ModerationHistory,
  ModerationStats,
  VendorVerification,
  VendorVerificationListParams,
  VendorVerificationListResponse,
  VendorVerificationAction,
  // Analytics
  AnalyticsRequest,
  UserAnalytics,
  EngagementAnalytics,
  ContentAnalytics,
  MarketplaceAnalytics,
  SystemHealth,
  AdminDashboardSummary,
  AuditLogListParams,
  AuditLogListResponse,
  // Resources
  AdminResource,
  AdminResourceListItem,
  ResourceListParams,
  ResourceListResponse,
  CreateResource,
  UpdateResource,
  ResourceCategory,
} from '@/types/admin';

// =============================================================================
// Base Configuration
// =============================================================================

const ADMIN_BASE = '/admin';

// =============================================================================
// User Management API
// =============================================================================

// Helper to transform snake_case backend user data to camelCase frontend format
interface BackendUserListItem {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  is_email_verified: boolean;
  created_at: string;
  last_login_at: string | null;
  family_count?: number;
  profile_image_url?: string | null;
}

interface BackendUserListResponse {
  items: BackendUserListItem[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

interface BackendUserDetail {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string | null;
  role: string;
  status: string;
  is_email_verified: boolean;
  email_verified_at?: string | null;
  profile_image_url?: string | null;
  timezone?: string | null;
  language?: string;
  created_at: string;
  updated_at: string;
  last_login_at?: string | null;
  suspended_at?: string | null;
  suspended_by_id?: string | null;
  suspension_reason?: string | null;
  suspension_expires_at?: string | null;
  families?: Array<{
    id: string;
    name: string;
    member_count: number;
    child_count: number;
    created_at: string;
  }>;
  recent_logins?: Array<{
    logged_in_at: string;
    ip_address?: string | null;
    user_agent?: string | null;
    success: boolean;
  }>;
  warning_count?: number;
  flag_count?: number;
  metadata?: Record<string, unknown> | null;
}

// Map backend status to frontend status
const statusMap: Record<string, string> = {
  active: 'active',
  suspended: 'suspended',
  pending: 'pending_verification',
  deleted: 'deactivated',
};

// Map backend role to frontend role
const roleMap: Record<string, string> = {
  family: 'family_admin',
  attorney: 'attorney',
  vendor: 'vendor',
  healthcare_provider: 'provider',
  moderator: 'admin',
  admin: 'admin',
  super_admin: 'super_admin',
};

function transformUserDetail(item: BackendUserDetail): AdminUser {
  const mappedRole = roleMap[item.role?.toLowerCase()] || item.role || 'family_admin';
  const mappedStatus = statusMap[item.status?.toLowerCase()] || 'pending_verification';

  return {
    id: item.id,
    email: item.email,
    firstName: item.first_name || '',
    lastName: item.last_name || '',
    fullName: `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown',
    avatarUrl: item.profile_image_url || null,
    role: mappedRole as AdminUser['role'],
    status: mappedStatus as AdminUser['status'],
    emailVerified: item.is_email_verified,
    emailVerifiedAt: item.email_verified_at || null,
    phoneNumber: item.phone || null,
    phoneVerified: false, // Backend doesn't have this field
    lastLoginAt: item.last_login_at || null,
    lastActiveAt: item.last_login_at || null, // Use last_login as proxy
    loginCount: item.recent_logins?.length || 0,
    failedLoginAttempts: 0, // Backend doesn't track this in detail response
    lockedUntil: null,
    mfaEnabled: false, // Backend doesn't have this field
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    suspendedAt: item.suspended_at || null,
    suspensionReason: item.suspension_reason ? 'other' as const : null,
    suspensionNotes: item.suspension_reason || null,
    metadata: item.metadata || null,
  };
}

function transformUserListItem(item: BackendUserListItem) {
  const mappedRole = roleMap[item.role?.toLowerCase()] || item.role || 'family_admin';
  const mappedStatus = statusMap[item.status?.toLowerCase()] || 'pending_verification';

  return {
    id: item.id,
    email: item.email,
    fullName: `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown',
    avatarUrl: item.profile_image_url || null,
    role: mappedRole as AdminUserListResponse['items'][0]['role'],
    status: mappedStatus as AdminUserListResponse['items'][0]['status'],
    emailVerified: item.is_email_verified,
    lastLoginAt: item.last_login_at,
    createdAt: item.created_at,
    familyCount: item.family_count,
  };
}

export const adminUsersApi = {
  /**
   * List all users with filtering and pagination
   */
  list: async (params: AdminUserListParams): Promise<AdminUserListResponse> => {
    const response = await apiClient.get<BackendUserListResponse>(`${ADMIN_BASE}/users`, {
      params,
    });

    // Transform backend snake_case to frontend camelCase
    return {
      items: response.data.items.map(transformUserListItem),
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.pages,
    };
  },

  /**
   * Get a single user's details
   */
  get: async (userId: string): Promise<AdminUser> => {
    const response = await apiClient.get<BackendUserDetail>(`${ADMIN_BASE}/users/${userId}`);
    return transformUserDetail(response.data);
  },

  /**
   * Update user details
   */
  update: async (userId: string, data: UpdateAdminUser): Promise<AdminUser> => {
    const response = await apiClient.patch<BackendUserDetail>(
      `${ADMIN_BASE}/users/${userId}`,
      data
    );
    return transformUserDetail(response.data);
  },

  /**
   * Suspend a user
   */
  suspend: async (userId: string, data: SuspendUserRequest): Promise<AdminUser> => {
    const response = await apiClient.post<BackendUserDetail>(
      `${ADMIN_BASE}/users/${userId}/suspend`,
      data
    );
    return transformUserDetail(response.data);
  },

  /**
   * Activate a suspended user
   */
  activate: async (userId: string): Promise<AdminUser> => {
    const response = await apiClient.post<BackendUserDetail>(
      `${ADMIN_BASE}/users/${userId}/activate`
    );
    return transformUserDetail(response.data);
  },

  /**
   * Delete a user (soft delete)
   */
  delete: async (userId: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_BASE}/users/${userId}`);
  },

  /**
   * Get user activity log
   */
  getActivity: async (
    userId: string,
    params: UserActivityListParams
  ): Promise<UserActivityListResponse> => {
    const response = await apiClient.get<UserActivityListResponse>(
      `${ADMIN_BASE}/users/${userId}/activity`,
      { params }
    );
    return response.data;
  },

  /**
   * Get user statistics
   */
  getStats: async (): Promise<UserStats> => {
    const response = await apiClient.get<UserStats>(`${ADMIN_BASE}/users/stats`);
    return response.data;
  },

  /**
   * Export users to CSV
   */
  export: async (params: AdminUserListParams): Promise<Blob> => {
    const response = await apiClient.get(`${ADMIN_BASE}/users/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Reset user password (sends reset email)
   */
  resetPassword: async (userId: string): Promise<void> => {
    await apiClient.post(`${ADMIN_BASE}/users/${userId}/reset-password`);
  },

  /**
   * Impersonate user (for debugging)
   */
  impersonate: async (userId: string): Promise<{ token: string }> => {
    const response = await apiClient.post<{ token: string }>(
      `${ADMIN_BASE}/users/${userId}/impersonate`
    );
    return response.data;
  },
};

// =============================================================================
// Moderation API
// =============================================================================

export const adminModerationApi = {
  /**
   * Get moderation queue
   */
  getQueue: async (params: ModerationQueueParams): Promise<ModerationQueueResponse> => {
    const response = await apiClient.get<ModerationQueueResponse>(
      `${ADMIN_BASE}/moderation/queue`,
      { params }
    );
    return response.data;
  },

  /**
   * Get a single moderation item
   */
  getItem: async (itemId: string): Promise<ModerationItem> => {
    const response = await apiClient.get<ModerationItem>(
      `${ADMIN_BASE}/moderation/items/${itemId}`
    );
    return response.data;
  },

  /**
   * Take action on a moderation item
   */
  takeAction: async (
    itemId: string,
    action: ModerationActionRequest
  ): Promise<ModerationItem> => {
    const response = await apiClient.post<ModerationItem>(
      `${ADMIN_BASE}/moderation/items/${itemId}/action`,
      action
    );
    return response.data;
  },

  /**
   * Bulk approve items
   */
  bulkApprove: async (itemIds: string[]): Promise<{ processed: number }> => {
    const response = await apiClient.post<{ processed: number }>(
      `${ADMIN_BASE}/moderation/bulk-approve`,
      { itemIds }
    );
    return response.data;
  },

  /**
   * Bulk reject items
   */
  bulkReject: async (
    itemIds: string[],
    reason?: string
  ): Promise<{ processed: number }> => {
    const response = await apiClient.post<{ processed: number }>(
      `${ADMIN_BASE}/moderation/bulk-reject`,
      { itemIds, reason }
    );
    return response.data;
  },

  /**
   * Get moderation history for an item
   */
  getHistory: async (itemId: string): Promise<ModerationHistory[]> => {
    const response = await apiClient.get<ModerationHistory[]>(
      `${ADMIN_BASE}/moderation/items/${itemId}/history`
    );
    return response.data;
  },

  /**
   * Get moderation statistics
   */
  getStats: async (): Promise<ModerationStats> => {
    const response = await apiClient.get<ModerationStats>(
      `${ADMIN_BASE}/moderation/stats`
    );
    return response.data;
  },

  // Vendor Verification
  /**
   * List pending vendor verifications
   */
  listVendorVerifications: async (
    params: VendorVerificationListParams
  ): Promise<VendorVerificationListResponse> => {
    const response = await apiClient.get<VendorVerificationListResponse>(
      `${ADMIN_BASE}/vendors/pending`,
      { params }
    );
    return response.data;
  },

  /**
   * Get vendor verification details
   */
  getVendorVerification: async (vendorId: string): Promise<VendorVerification> => {
    const response = await apiClient.get<VendorVerification>(
      `${ADMIN_BASE}/vendors/${vendorId}/verification`
    );
    return response.data;
  },

  /**
   * Take action on vendor verification
   */
  verifyVendor: async (
    vendorId: string,
    action: VendorVerificationAction
  ): Promise<VendorVerification> => {
    const response = await apiClient.post<VendorVerification>(
      `${ADMIN_BASE}/vendors/${vendorId}/verify`,
      action
    );
    return response.data;
  },
};

// =============================================================================
// Resources API
// =============================================================================

export const adminResourcesApi = {
  /**
   * List resources
   */
  list: async (params: ResourceListParams): Promise<ResourceListResponse> => {
    const response = await apiClient.get<ResourceListResponse>(
      `${ADMIN_BASE}/resources`,
      { params }
    );
    return response.data;
  },

  /**
   * Get a single resource
   */
  get: async (resourceId: string): Promise<AdminResource> => {
    const response = await apiClient.get<AdminResource>(
      `${ADMIN_BASE}/resources/${resourceId}`
    );
    return response.data;
  },

  /**
   * Create a new resource
   */
  create: async (data: CreateResource): Promise<AdminResource> => {
    const response = await apiClient.post<AdminResource>(
      `${ADMIN_BASE}/resources`,
      data
    );
    return response.data;
  },

  /**
   * Update a resource
   */
  update: async (resourceId: string, data: UpdateResource): Promise<AdminResource> => {
    const response = await apiClient.patch<AdminResource>(
      `${ADMIN_BASE}/resources/${resourceId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a resource
   */
  delete: async (resourceId: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_BASE}/resources/${resourceId}`);
  },

  /**
   * Publish a resource
   */
  publish: async (resourceId: string): Promise<AdminResource> => {
    const response = await apiClient.post<AdminResource>(
      `${ADMIN_BASE}/resources/${resourceId}/publish`
    );
    return response.data;
  },

  /**
   * Unpublish a resource
   */
  unpublish: async (resourceId: string): Promise<AdminResource> => {
    const response = await apiClient.post<AdminResource>(
      `${ADMIN_BASE}/resources/${resourceId}/unpublish`
    );
    return response.data;
  },

  /**
   * Get categories
   */
  getCategories: async (): Promise<ResourceCategory[]> => {
    const response = await apiClient.get<ResourceCategory[]>(
      `${ADMIN_BASE}/resources/categories`
    );
    return response.data;
  },

  /**
   * Upload featured image
   */
  uploadImage: async (resourceId: string, file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ url: string }>(
      `${ADMIN_BASE}/resources/${resourceId}/image`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },
};

// =============================================================================
// Analytics API
// =============================================================================

export const adminAnalyticsApi = {
  /**
   * Get dashboard summary (overview)
   */
  getDashboard: async (): Promise<AdminDashboardSummary> => {
    const response = await apiClient.get<AdminDashboardSummary>(
      `${ADMIN_BASE}/analytics/overview`
    );
    return response.data;
  },

  /**
   * Get user analytics
   */
  getUserAnalytics: async (params: AnalyticsRequest): Promise<UserAnalytics> => {
    const response = await apiClient.get<UserAnalytics>(
      `${ADMIN_BASE}/analytics/users`,
      { params }
    );
    return response.data;
  },

  /**
   * Get engagement analytics
   */
  getEngagementAnalytics: async (
    params: AnalyticsRequest
  ): Promise<EngagementAnalytics> => {
    const response = await apiClient.get<EngagementAnalytics>(
      `${ADMIN_BASE}/analytics/engagement`,
      { params }
    );
    return response.data;
  },

  /**
   * Get content analytics
   */
  getContentAnalytics: async (params: AnalyticsRequest): Promise<ContentAnalytics> => {
    const response = await apiClient.get<ContentAnalytics>(
      `${ADMIN_BASE}/analytics/content`,
      { params }
    );
    return response.data;
  },

  /**
   * Get marketplace analytics
   */
  getMarketplaceAnalytics: async (
    params: AnalyticsRequest
  ): Promise<MarketplaceAnalytics> => {
    const response = await apiClient.get<MarketplaceAnalytics>(
      `${ADMIN_BASE}/analytics/marketplace`,
      { params }
    );
    return response.data;
  },

  /**
   * Export analytics report
   */
  exportReport: async (
    reportType: 'users' | 'engagement' | 'content' | 'marketplace',
    params: AnalyticsRequest
  ): Promise<Blob> => {
    const response = await apiClient.get(
      `${ADMIN_BASE}/analytics/${reportType}/export`,
      {
        params,
        responseType: 'blob',
      }
    );
    return response.data;
  },
};

// =============================================================================
// System API
// =============================================================================

export const adminSystemApi = {
  /**
   * Get system health status
   */
  getHealth: async (): Promise<SystemHealth> => {
    const response = await apiClient.get<SystemHealth>(`${ADMIN_BASE}/system/health`);
    return response.data;
  },

  /**
   * Get background jobs status
   */
  getJobs: async (): Promise<{
    items: Array<{
      id: string;
      name: string;
      task_name: string;
      status: 'pending' | 'started' | 'success' | 'failure' | 'retry' | 'revoked';
      priority: string;
      created_at: string;
      started_at?: string;
      completed_at?: string;
      runtime_seconds?: number;
      queue_name: string;
      args?: unknown[];
      kwargs?: Record<string, unknown>;
      error?: string;
    }>;
    total: number;
    page: number;
    page_size: number;
    pages: number;
  }> => {
    const response = await apiClient.get(`${ADMIN_BASE}/system/jobs`);
    return response.data;
  },

  /**
   * Retry failed job
   */
  retryJob: async (jobId: string): Promise<void> => {
    await apiClient.post(`${ADMIN_BASE}/system/jobs/${jobId}/retry`);
  },

  /**
   * Clear failed jobs
   */
  clearFailedJobs: async (): Promise<{ cleared: number }> => {
    const response = await apiClient.post<{ cleared: number }>(
      `${ADMIN_BASE}/system/jobs/clear-failed`
    );
    return response.data;
  },

  /**
   * Get audit logs
   */
  getAuditLogs: async (params: AuditLogListParams): Promise<AuditLogListResponse> => {
    const response = await apiClient.get<AuditLogListResponse>(
      `${ADMIN_BASE}/audit-logs`,
      { params }
    );
    return response.data;
  },

  /**
   * Export audit logs
   */
  exportAuditLogs: async (params: AuditLogListParams): Promise<Blob> => {
    const response = await apiClient.get(`${ADMIN_BASE}/audit-logs/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Trigger cache clear
   */
  clearCache: async (cacheType?: string): Promise<{ cleared: boolean }> => {
    const response = await apiClient.post<{ cleared: boolean }>(
      `${ADMIN_BASE}/system/cache/clear`,
      { cacheType }
    );
    return response.data;
  },

  /**
   * Get system settings
   */
  getSettings: async (): Promise<Record<string, unknown>> => {
    const response = await apiClient.get(`${ADMIN_BASE}/system/settings`);
    return response.data;
  },

  /**
   * Update system settings
   */
  updateSettings: async (
    settings: Record<string, unknown>
  ): Promise<Record<string, unknown>> => {
    const response = await apiClient.patch(`${ADMIN_BASE}/system/settings`, settings);
    return response.data;
  },
};

// =============================================================================
// Combined Admin API Export
// =============================================================================

export const adminApi = {
  users: adminUsersApi,
  moderation: adminModerationApi,
  resources: adminResourcesApi,
  analytics: adminAnalyticsApi,
  system: adminSystemApi,
};

export default adminApi;
